import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Container,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notes as NotesIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  TransferWithinAStation as TransferIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WaitingListEntry, getWaitingList, addToWaitingList, updateWaitingListEntry, removeFromWaitingList, updatePriority, reorderWaitingList } from '../../firebase/services/waitingListService';
import { format } from 'date-fns';
import ContactNotesDialog from '../../components/waiting-list/ContactNotesDialog';
import TransferToSlipDialog from '../../components/waiting-list/TransferToSlipDialog';
import { createCustomer, getCustomerByEmail } from '../../firebase/services/customerService';

const formatDate = (date: Date | { toDate: () => Date }) => {
  const dateObj = date instanceof Date ? date : date.toDate();
  return format(dateObj, 'MMM d, yyyy');
};

const getStatusColor = (status: WaitingListEntry['status']) => {
  switch (status) {
    case 'pending': return 'default';
    case 'approved': return 'success';
    case 'rejected': return 'error';
    default: return 'default';
  }
};

interface SortableRowProps {
  entry: WaitingListEntry;
  index: number;
  onEdit: (entry: WaitingListEntry) => void;
  onTransfer: (entry: WaitingListEntry) => void;
}

const SortableRow = ({ entry, index, onEdit, onTransfer }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: entry.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative' as const,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <TableCell>
        <Box {...listeners} sx={{ cursor: 'grab' }}>
          <DragIcon />
        </Box>
      </TableCell>
      <TableCell>
        {format(entry.dateAdded, 'MMM d, yyyy')}
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2">
            {entry.firstName} {entry.lastName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {entry.customerEmail}
          </Typography>
          <br />
          <Typography variant="caption" color="textSecondary">
            {entry.customerPhone}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{entry.vesselName}</Typography>
        <Typography variant="caption" color="textSecondary">
          {entry.vesselLength}' - {entry.vesselYear} {entry.vesselMake} {entry.vesselModel}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={entry.status}
          color={getStatusColor(entry.status)}
          size="small"
        />
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          onClick={() => onEdit(entry)}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onTransfer(entry)}
          disabled={entry.status !== 'approved'}
        >
          <TransferIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const WaitingListManagement: React.FC = () => {
  const [entries, setEntries] = useState<WaitingListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WaitingListEntry | null>(null);
  const [selectedEntryForNotes, setSelectedEntryForNotes] = useState<WaitingListEntry | null>(null);
  const [selectedEntryForTransfer, setSelectedEntryForTransfer] = useState<WaitingListEntry | null>(null);
  const [formData, setFormData] = useState<Partial<WaitingListEntry>>({
    firstName: '',
    lastName: '',
    customerEmail: '',
    customerPhone: '',
    cellPhone: '',
    isHomeowner: undefined,
    mailingAddress: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    localAddress: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    vesselName: '',
    vesselYear: '',
    vesselMake: '',
    vesselModel: '',
    vesselColor: '',
    vesselLength: 0,
    vesselRegistrationNumber: '',
    vesselRegistrationState: '',
    vesselRegistrationYear: '',
    notes: ''
  });
  const [success, setSuccess] = useState(false);
  const [openNewEntryDialog, setOpenNewEntryDialog] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadWaitingList();
  }, []);

  const loadWaitingList = async () => {
    try {
      setLoading(true);
      const data = await getWaitingList();
      setEntries(data);
      setError(null);
    } catch (err) {
      setError('Failed to load waiting list');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (entry?: WaitingListEntry) => {
    if (entry) {
      setSelectedEntry(entry);
      setFormData({
        firstName: entry.firstName,
        lastName: entry.lastName,
        customerEmail: entry.customerEmail,
        customerPhone: entry.customerPhone || '',
        cellPhone: entry.cellPhone,
        isHomeowner: entry.isHomeowner,
        mailingAddress: {
          street: entry.mailingAddress.street,
          city: entry.mailingAddress.city,
          state: entry.mailingAddress.state,
          zip: entry.mailingAddress.zip
        },
        localAddress: {
          street: entry.localAddress.street,
          city: entry.localAddress.city,
          state: entry.localAddress.state,
          zip: entry.localAddress.zip
        },
        vesselName: entry.vesselName,
        vesselYear: entry.vesselYear,
        vesselMake: entry.vesselMake,
        vesselModel: entry.vesselModel,
        vesselColor: entry.vesselColor,
        vesselLength: entry.vesselLength,
        vesselRegistrationNumber: entry.vesselRegistrationNumber,
        vesselRegistrationState: entry.vesselRegistrationState,
        vesselRegistrationYear: entry.vesselRegistrationYear,
        notes: entry.notes || '',
        status: entry.status,
        priority: entry.priority
      });
    } else {
      setSelectedEntry(null);
      setFormData({
        firstName: '',
        lastName: '',
        customerEmail: '',
        customerPhone: '',
        cellPhone: '',
        isHomeowner: undefined,
        mailingAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        localAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        vesselName: '',
        vesselYear: '',
        vesselMake: '',
        vesselModel: '',
        vesselColor: '',
        vesselLength: 0,
        vesselRegistrationNumber: '',
        vesselRegistrationState: '',
        vesselRegistrationYear: '',
        notes: '',
        status: 'pending',
        priority: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEntry(null);
    setFormData({
      firstName: '',
      lastName: '',
      customerEmail: '',
      customerPhone: '',
      cellPhone: '',
      isHomeowner: undefined,
      mailingAddress: {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      localAddress: {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      vesselName: '',
      vesselYear: '',
      vesselMake: '',
      vesselModel: '',
      vesselColor: '',
      vesselLength: 0,
      vesselRegistrationNumber: '',
      vesselRegistrationState: '',
      vesselRegistrationYear: '',
      notes: '',
      status: 'pending',
      priority: 0
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate form data
      if (!formData.firstName || !formData.lastName || !formData.customerEmail || !formData.cellPhone) {
        throw new Error('Please fill in all required fields');
      }

      // Validate local address if homeowner
      if (formData.isHomeowner === true) {
        if (!formData.localAddress.street || !formData.localAddress.city || 
            !formData.localAddress.state || !formData.localAddress.zip) {
          throw new Error('Please provide your local address as a homeowner');
        }
      }

      if (!formData.vesselName || !formData.vesselLength || !formData.vesselYear || 
          !formData.vesselMake || !formData.vesselModel || !formData.vesselColor) {
        throw new Error('Please provide all vessel details');
      }

      if (selectedEntry) {
        // Update existing entry
        await updateWaitingListEntry(selectedEntry.id!, {
          ...formData,
          vesselLength: parseFloat(formData.vesselLength.toString())
        });
        setSuccess(true);
        setOpenDialog(false);
        loadWaitingList();
      } else {
        // Check if customer exists
        let customerId: string;
        const existingCustomer = await getCustomerByEmail(formData.customerEmail);
        
        if (existingCustomer) {
          customerId = existingCustomer.id!;
        } else {
          // Create new customer
          const newCustomer = await createCustomer({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.customerEmail,
            phone: formData.customerPhone,
            status: 'active',
            dateCreated: new Date()
          });
          customerId = newCustomer.id!;
        }

        // Convert numeric fields and add to waiting list
        const entry: Omit<WaitingListEntry, 'id' | 'dateAdded'> = {
          firstName: formData.firstName!,
          lastName: formData.lastName!,
          customerEmail: formData.customerEmail!,
          customerPhone: formData.customerPhone || '',
          cellPhone: formData.cellPhone!,
          isHomeowner: formData.isHomeowner,
          mailingAddress: formData.mailingAddress!,
          localAddress: formData.localAddress!,
          vesselName: formData.vesselName!,
          vesselYear: formData.vesselYear!,
          vesselMake: formData.vesselMake!,
          vesselModel: formData.vesselModel!,
          vesselColor: formData.vesselColor!,
          vesselLength: parseFloat(formData.vesselLength.toString()),
          vesselRegistrationNumber: formData.vesselRegistrationNumber!,
          vesselRegistrationState: formData.vesselRegistrationState!,
          vesselRegistrationYear: formData.vesselRegistrationYear!,
          notes: formData.notes || '',
          customerId,
          status: 'pending',
          priority: 0
        };

        await addToWaitingList(entry);
        setSuccess(true);
        setOpenNewEntryDialog(false);
      }
      
      // Reset form data
      setFormData({
        firstName: '',
        lastName: '',
        customerEmail: '',
        customerPhone: '',
        cellPhone: '',
        isHomeowner: undefined,
        mailingAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        localAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        vesselName: '',
        vesselYear: '',
        vesselMake: '',
        vesselModel: '',
        vesselColor: '',
        vesselLength: 0,
        vesselRegistrationNumber: '',
        vesselRegistrationState: '',
        vesselRegistrationYear: '',
        notes: '',
        status: 'pending',
        priority: 0
      });
      // Refresh the waiting list
      loadWaitingList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this entry from the waiting list?')) {
      try {
        await removeFromWaitingList(id);
        loadWaitingList();
      } catch (err) {
        setError('Failed to delete entry');
      }
    }
  };

  const handlePriorityChange = async (id: string, change: number) => {
    try {
      const entry = entries.find(e => e.id === id);
      if (entry) {
        const newPriority = entry.priority + change;
        await updatePriority(id, newPriority);
        loadWaitingList();
      }
    } catch (err) {
      setError('Failed to update priority');
    }
  };

  const handleStatusChange = (entry: WaitingListEntry, newStatus: WaitingListEntry['status']) => {
    updateWaitingListEntry(entry.id!, { status: newStatus });
    setEntries(entries.map(e => 
      e.id === entry.id ? { ...e, status: newStatus } : e
    ));
  };

  const handleNotesClick = (entry: WaitingListEntry) => {
    setSelectedEntryForNotes(entry);
  };

  const handleTransferToSlip = (entry: WaitingListEntry) => {
    setSelectedEntryForNotes(null);
    setSelectedEntryForTransfer(entry);
  };

  const handleTransferComplete = async (entry: WaitingListEntry, slipId: string, startDate: Date) => {
    try {
      // Here you would implement the logic to create a new slip contract
      // and remove the entry from the waiting list
      await removeFromWaitingList(entry.id!);
      setSelectedEntryForTransfer(null);
      loadWaitingList();
    } catch (err) {
      setError('Failed to complete transfer');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as Record<string, any>),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = entries.findIndex(entry => entry.id === active.id);
      const newIndex = entries.findIndex(entry => entry.id === over.id);
      
      const newEntries = arrayMove(entries, oldIndex, newIndex);
      setEntries(newEntries);
      
      try {
        await reorderWaitingList(newEntries.map(entry => entry.id!));
      } catch (err) {
        setError('Failed to reorder waiting list');
        loadWaitingList();
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            Waiting List Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewEntryDialog(true)}
          >
            New Entry
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            New entry added successfully!
          </Alert>
        )}

        <Paper sx={{ mb: 4 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Date Added</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Vessel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext
                    items={entries.map(entry => entry.id!)}
                    strategy={verticalListSortingStrategy}
                  >
                    {entries.map((entry, index) => (
                      <SortableRow
                        key={entry.id}
                        entry={entry}
                        index={index}
                        onEdit={handleOpenDialog}
                        onTransfer={setSelectedEntryForTransfer}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </TableContainer>
          </DndContext>
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedEntry ? 'Edit Waiting List Entry' : 'Add New Waiting List Entry'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ display: 'flex', alignItems: 'center' }}>
                  <InputLabel sx={{ textAlign: 'center', width: '100%' }}>
                    Are you a homeowner in the Fallen Leaf basin?
                  </InputLabel>
                  <Select
                    value={formData.isHomeowner === undefined ? '' : formData.isHomeowner ? 'true' : 'false'}
                    label="Are you a homeowner in the Fallen Leaf basin?"
                    onChange={(e) => setFormData({ ...formData, isHomeowner: e.target.value === 'true' })}
                    sx={{ textAlign: 'center' }}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Home Phone"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cell Phone"
                  value={formData.cellPhone}
                  onChange={(e) => setFormData({ ...formData, cellPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Mailing Address
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.mailingAddress?.street || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        mailingAddress: { 
                          ...formData.mailingAddress!, 
                          street: e.target.value 
                        } 
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.mailingAddress?.city || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        mailingAddress: { 
                          ...formData.mailingAddress!, 
                          city: e.target.value 
                        } 
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.mailingAddress?.state || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        mailingAddress: { 
                          ...formData.mailingAddress!, 
                          state: e.target.value 
                        } 
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.mailingAddress?.zip || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        mailingAddress: { 
                          ...formData.mailingAddress!, 
                          zip: e.target.value 
                        } 
                      })}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Local Address (if different from mailing)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.localAddress?.street || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        localAddress: { 
                          ...formData.localAddress!, 
                          street: e.target.value 
                        } 
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.localAddress?.city || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        localAddress: { 
                          ...formData.localAddress!, 
                          city: e.target.value 
                        } 
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.localAddress?.state || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        localAddress: { 
                          ...formData.localAddress!, 
                          state: e.target.value 
                        } 
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.localAddress?.zip || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        localAddress: { 
                          ...formData.localAddress!, 
                          zip: e.target.value 
                        } 
                      })}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Email"
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Name"
                  value={formData.vesselName}
                  onChange={(e) => setFormData({ ...formData, vesselName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Year"
                  value={formData.vesselYear}
                  onChange={(e) => setFormData({ ...formData, vesselYear: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Make"
                  value={formData.vesselMake}
                  onChange={(e) => setFormData({ ...formData, vesselMake: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Model"
                  value={formData.vesselModel}
                  onChange={(e) => setFormData({ ...formData, vesselModel: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Color"
                  value={formData.vesselColor}
                  onChange={(e) => setFormData({ ...formData, vesselColor: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Length (ft)"
                  type="number"
                  value={formData.vesselLength}
                  onChange={(e) => setFormData({ ...formData, vesselLength: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Vessel Registration #"
                  value={formData.vesselRegistrationNumber}
                  onChange={(e) => setFormData({ ...formData, vesselRegistrationNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Vessel Registration State"
                  value={formData.vesselRegistrationState}
                  onChange={(e) => setFormData({ ...formData, vesselRegistrationState: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Vessel Registration Year"
                  value={formData.vesselRegistrationYear}
                  onChange={(e) => setFormData({ ...formData, vesselRegistrationYear: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status || 'pending'}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as WaitingListEntry['status'] })}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Priority"
                  type="number"
                  value={formData.priority || 0}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {selectedEntry ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {selectedEntryForNotes && (
          <ContactNotesDialog
            open={!!selectedEntryForNotes}
            onClose={() => setSelectedEntryForNotes(null)}
            entry={selectedEntryForNotes}
            onTransferToSlip={handleTransferToSlip}
          />
        )}

        {selectedEntryForTransfer && (
          <TransferToSlipDialog
            open={!!selectedEntryForTransfer}
            onClose={() => setSelectedEntryForTransfer(null)}
            entry={selectedEntryForTransfer}
            onTransfer={handleTransferComplete}
          />
        )}

        <Dialog 
          open={openNewEntryDialog} 
          onClose={() => setOpenNewEntryDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add New Waiting List Entry</DialogTitle>
          <DialogContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Contact Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="customerEmail"
                        type="email"
                        value={formData.customerEmail}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Home Phone"
                        name="customerPhone"
                        value={formData.customerPhone}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cell Phone"
                        name="cellPhone"
                        value={formData.cellPhone}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Mailing Address
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        name="mailingAddress.street"
                        value={formData.mailingAddress.street}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        name="mailingAddress.city"
                        value={formData.mailingAddress.city}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="State"
                        name="mailingAddress.state"
                        value={formData.mailingAddress.state}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        name="mailingAddress.zip"
                        value={formData.mailingAddress.zip}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Local Address {formData.isHomeowner === true && '(Required for homeowners)'}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FormLabel component="legend" sx={{ textAlign: 'center', width: '100%' }}>
                          Are you a homeowner in the Fallen Leaf basin? *
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.isHomeowner === undefined ? '' : formData.isHomeowner ? 'true' : 'false'}
                          onChange={(e) => setFormData({ ...formData, isHomeowner: e.target.value === 'true' })}
                          sx={{ justifyContent: 'center' }}
                        >
                          <FormControlLabel value="true" control={<Radio />} label="Yes" />
                          <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        name="localAddress.street"
                        value={formData.localAddress.street}
                        onChange={handleChange}
                        required={formData.isHomeowner === true}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        name="localAddress.city"
                        value={formData.localAddress.city}
                        onChange={handleChange}
                        required={formData.isHomeowner === true}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="State"
                        name="localAddress.state"
                        value={formData.localAddress.state}
                        onChange={handleChange}
                        required={formData.isHomeowner === true}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        name="localAddress.zip"
                        value={formData.localAddress.zip}
                        onChange={handleChange}
                        required={formData.isHomeowner === true}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Vessel Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Vessel Name"
                        name="vesselName"
                        value={formData.vesselName}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Year"
                        name="vesselYear"
                        value={formData.vesselYear}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Make"
                        name="vesselMake"
                        value={formData.vesselMake}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Model"
                        name="vesselModel"
                        value={formData.vesselModel}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Color"
                        name="vesselColor"
                        value={formData.vesselColor}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Length (ft)"
                        name="vesselLength"
                        type="number"
                        value={formData.vesselLength}
                        onChange={handleChange}
                        inputProps={{ min: 0, step: 0.1 }}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Registration #"
                        name="vesselRegistrationNumber"
                        value={formData.vesselRegistrationNumber}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Registration State"
                        name="vesselRegistrationState"
                        value={formData.vesselRegistrationState}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vessel Registration Year"
                        name="vesselRegistrationYear"
                        value={formData.vesselRegistrationYear}
                        onChange={handleChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Additional Notes"
                        name="notes"
                        multiline
                        rows={4}
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Please provide any additional information that might help us find the right slip for you."
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewEntryDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Add Entry'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default WaitingListManagement; 